import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Gift, ArrowRight } from 'lucide-react';
import { BACKEND_URL } from '../config/api';

const fmtCities = (cities) => {
  if (!cities.length) return '';
  const titled = cities.map((c) => c.replace(/\b\w/g, (m) => m.toUpperCase()));
  if (titled.length === 1) return titled[0];
  if (titled.length === 2) return `${titled[0]} and ${titled[1]}`;
  return `${titled.slice(0, 2).join(', ')} and ${titled.length - 2} more ${titled.length - 2 === 1 ? 'city' : 'cities'}`;
};

/** Landing-page promo for the live Sign-Up Bonus campaign. Renders nothing when none is running. */
const SignUpBonusPromo = ({ onCta }) => {
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    let active = true;
    axios.get(`${BACKEND_URL}/api/signup-bonus/promo`)
      .then((r) => { if (active && r.data?.active) setPromo(r.data); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  if (!promo) return null;

  const where = promo.nationwide ? 'across Canada' : (promo.cities.length ? `in ${fmtCities(promo.cities)}` : '');

  return (
    <section className="sbpromo" data-testid="landing-signup-bonus-promo">
      <div className="sbpromo-inner">
        <div className="sbpromo-icon"><Gift size={26} /></div>
        <div className="sbpromo-copy">
          <span className="sbpromo-eyebrow">New on taskeri<sup>®</sup></span>
          <h2 className="sbpromo-title" data-testid="signup-bonus-promo-amount">
            Get ${Number(promo.bonus_amount).toFixed(0)} toward your first task {where && <span className="sbpromo-where">{where}</span>}
          </h2>
          <p className="sbpromo-desc">
            Sign up today and we'll drop a ${Number(promo.bonus_amount).toFixed(0)} Sign-Up Bonus into your wallet.
            It applies automatically at checkout on any job of ${Number(promo.min_job_value).toFixed(0)} or more.
          </p>
        </div>
        <div className="sbpromo-actions">
          <button className="sbpromo-cta" onClick={onCta} data-testid="signup-bonus-promo-cta">
            Claim your bonus <ArrowRight size={17} />
          </button>
          <a href="/sign-up-bonus-terms" className="sbpromo-terms" data-testid="signup-bonus-promo-terms">
            See bonus terms
          </a>
        </div>
      </div>

      <style>{`
        .sbpromo { padding: 0 20px 10px; background: transparent; }
        .sbpromo-inner {
          max-width: 1120px; margin: 0 auto; display: flex; align-items: center; gap: 28px;
          background: #2e1065; border: 1px solid #4c1d95; border-radius: 20px;
          padding: 30px 34px; position: relative; overflow: hidden;
        }
        .sbpromo-inner::after {
          content: ''; position: absolute; right: -60px; top: -60px; width: 220px; height: 220px;
          border-radius: 50%; background: rgba(132, 72, 252, 0.28); filter: blur(40px); pointer-events: none;
        }
        .sbpromo-icon {
          flex-shrink: 0; width: 56px; height: 56px; border-radius: 16px; display: grid; place-items: center;
          background: rgba(255,255,255,0.1); color: #ddd6fe; border: 1px solid rgba(221,214,254,0.3);
        }
        .sbpromo-copy { flex: 1; min-width: 0; }
        .sbpromo .sbpromo-eyebrow {
          display: block; color: #c4b5fd !important; -webkit-text-fill-color: #c4b5fd; font-size: 0.75rem; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 7px;
        }
        .sbpromo .sbpromo-title { color: #ffffff !important; -webkit-text-fill-color: #ffffff; background: none; font-size: 1.5rem; font-weight: 700; margin: 0 0 8px; line-height: 1.3; }
        .sbpromo .sbpromo-where { color: #c4b5fd !important; -webkit-text-fill-color: #c4b5fd; }
        .sbpromo .sbpromo-desc { color: #d8d3e8 !important; -webkit-text-fill-color: #d8d3e8; font-size: 0.9375rem; line-height: 1.65; margin: 0; max-width: 60ch; }
        .sbpromo-actions { flex-shrink: 0; display: flex; flex-direction: column; align-items: stretch; gap: 10px; }
        .sbpromo-cta {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px; white-space: nowrap;
          background: #ffffff; color: #4c1d95; border: none; border-radius: 999px;
          padding: 14px 26px; font-size: 0.9375rem; font-weight: 700; cursor: pointer;
          transition: transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease;
        }
        .sbpromo-cta:hover { transform: translateY(-2px); box-shadow: 0 12px 28px rgba(0,0,0,0.3); background: #f5f3ff; }
        .sbpromo-terms { color: #c4b5fd; font-size: 0.8125rem; text-align: center; text-decoration: underline; }
        .sbpromo-terms:hover { color: #ffffff; }
        @media (max-width: 860px) {
          .sbpromo-inner { flex-direction: column; align-items: flex-start; gap: 20px; padding: 26px 22px; }
          .sbpromo-title { font-size: 1.25rem; }
          .sbpromo-actions { width: 100%; }
        }
      `}</style>
    </section>
  );
};

export default SignUpBonusPromo;
